import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../app/hooks';
import { RootState } from '../../app/store';
import HistoryRouting from '../../components/HistoryRouting';
import RequiredAsterisk from '../../components/RequiredAsterisk';
import Button from '../../components/button/Button';
import { UpdateBrandInfo } from '../../features/partnerSlice';
import { User } from '../../utils/type';
import AppPreviewPair from '../../components/appPreview/AppPreview';
import { descriptionSuggestions, tagSuggestions } from '../../assets/dummy';
import Spinner from '../../components/spinner/Spinner';
import { BsInfoCircle } from 'react-icons/bs';
import { BrandingFormData } from '../../utils/type';
import { TextFieldLoader } from '../../components/branding/BrandingInformation';
import { FileUpload } from '../../components/FileUpload/fileUpload';
import { ColorInput } from '../../components/colorPicker/colorPicker';

const OrgData = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [partnerId, setPartnerId] = useState<string | null>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [previewBanner, setPreviewBanner] = useState<string | null>(null);
  const [, setLogoX] = React.useState(10);

  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);

  const extractFileName = (url: any) => {
    return url.split('/').pop();
  };

  // Extract the first part of b2cLink (before first dot)
  const extractB2CLinkPrefix = (link: string | undefined): string => {
    if (!link) return '';
    // Remove https:// or http:// if present
    let cleanLink = link.replace(/^https?:\/\//, '');
    // Extract the first part before the first dot
    return cleanLink.split('.')[0] || '';
  };

  const normalizeB2cLink = (link: string | undefined): string => {
    if (!link) return '';
    const withoutProtocol = link.replace(/^https?:\/\//, '');
    return withoutProtocol.split('/')[0];
  };

  const ensureDomainWithSuffix = (link: string | undefined): string => {
    const domain = normalizeB2cLink(link);
    if (!domain) return '';
    return domain.includes('.') ? domain : `${domain}.saversapp.com`;
  };

  const formatB2cLinkForSubmission = (prefix: string | undefined): string => {
    if (!prefix) return '';
    const normalizedPrefix = prefix.trim().toLowerCase();
    if (!normalizedPrefix) return '';
    return `${normalizedPrefix}.saversapp.com`;
  };

  const toComparableB2cLink = (link: string | undefined): string => {
    return ensureDomainWithSuffix(link).toLowerCase();
  };

  const BrandInfoState = useSelector(
    (state: RootState) => state.partner.brandInfo
  );
  const loadingStates = useSelector(
    (state: RootState) => state.partner.loadingStates
  );
  const loadingBrandInfo = useSelector(
    (state: RootState) => state.partner.loadingStates['GetBrandInfoState']
  );

  const dispatch = useAppDispatch();

  const userDetails = localStorage.getItem('userDetails');
  const user = userDetails ? JSON.parse(userDetails) : '';
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    setError,
    clearErrors,
    control,
  } = useForm<BrandingFormData>({
    defaultValues: {
      partnerId,
    },
  });
  const descValue = watch('description') || '';
  const tagValue = watch('tag') || '';

  const storedB2cDomain = ensureDomainWithSuffix(BrandInfoState?.b2cLink);
  const storedB2cHref = storedB2cDomain ? `https://${storedB2cDomain}` : '';

  useEffect(() => {
    if (BrandInfoState) {
      // Extract custom part from full b2cLink URL (first part before first dot)
      const extractB2cLink = (fullUrl: string) => {
        if (!fullUrl) return '';
        // Remove https:// or http:// if present
        let cleanLink = fullUrl.replace(/^https?:\/\//, '');
        // Extract the first part before the first dot
        return cleanLink.split('.')[0] || '';
      };

      // Extract custom part from full appDeepLink URL
      const extractAppDeepLink = (fullUrl: string) => {
        if (!fullUrl) return '';
        return fullUrl.replace('https://earncashback.app.link/', '');
      };

      // Set values for brand info
      setValue('orgDisplayName', BrandInfoState.orgDisplayName);
      setValue(
        'appDeepLink',
        extractAppDeepLink(BrandInfoState.appDeepLink || '')
      );
      setValue('b2cLink', extractB2cLink(BrandInfoState.b2cLink || ''));
      setValue('logo', BrandInfoState.logo || '');
      setValue('logoMobile', BrandInfoState.logoMobile || '');
      setValue('logoMonochrome', BrandInfoState.logoMonochrome || '');
      setValue('tmCertificate', BrandInfoState.tmCertificate || '');
      setValue('additionalDocument', BrandInfoState.additionalDocument || '');
      setValue('logoBanner', BrandInfoState.logoBanner || '');
      setValue('visitWebsiteLink', BrandInfoState.visitWebsiteLink || '');
      setValue('logoNewsletter', BrandInfoState.logoNewsletter || '');
      setValue('primaryColor', BrandInfoState.primaryColor || undefined);
      setValue('secondaryColor', BrandInfoState.secondaryColor || undefined);
      setValue('tertiaryColor', BrandInfoState.tertiaryColor || undefined);
      setValue('quaternaryColor', BrandInfoState.quaternaryColor || undefined);
      setValue('tag', BrandInfoState.tag);
      setValue('description', BrandInfoState.description);
      setValue('einNumber', BrandInfoState.einNumber);
      setValue('websiteLink', BrandInfoState.websiteLink || '');
      setValue('facebookLink', BrandInfoState.facebookLink || '');
      setValue('instagramLink', BrandInfoState.instagramLink || '');
      setValue('other', BrandInfoState.other || '');

      // Set file upload fields with the filename extracted from URL
    }
  }, [BrandInfoState, setValue]);
  useEffect(() => {
    if (userDetails) {
      const userObject: User = JSON.parse(userDetails);
      setPartnerId(userObject.partnerId);
    }
  }, [userDetails]);

  const onsubmit = async (data: BrandingFormData) => {
    if (partnerId) {
      const brandFormData = new FormData();
      brandFormData.append('partnerId', partnerId);
      brandFormData.append('brandInfo', 'completed');

      // Handle regular text fields (excluding b2cLink and appDeepLink)
      const textFields: (keyof BrandingFormData)[] = [
        'orgDisplayName',
        'visitWebsiteLink',
        'tag',
        'description',
        'einNumber',
        'primaryColor',
        'secondaryColor',
        'tertiaryColor',
        'quaternaryColor',
      ];
      textFields.forEach(field => {
        if (data[field] !== BrandInfoState?.[field]) {
          brandFormData.append(field, String(data[field] || ''));
        }
      });

      // Handle b2cLink - send with full URL
      const submittedB2cLink = formatB2cLinkForSubmission(data.b2cLink);
      if (
        submittedB2cLink.toLowerCase() !==
        toComparableB2cLink(BrandInfoState?.b2cLink)
      ) {
        brandFormData.append('b2cLink', submittedB2cLink);
      }

      // Handle appDeepLink - send with full URL
      const fullAppDeepLink = data.appDeepLink
        ? `https://earncashback.app.link/${data.appDeepLink}`
        : '';
      if (fullAppDeepLink !== BrandInfoState?.appDeepLink) {
        brandFormData.append('appDeepLink', fullAppDeepLink);
      }
      const fileFields: (keyof BrandingFormData)[] = [
        'logo',
        'logoMobile',
        'logoMonochrome',
        'logoBanner',
        'icon',
        'logoNewsletter',
      ];
      fileFields.forEach(field => {
        const file = data?.[field]?.[0];
        if (file instanceof File) {
          brandFormData.append(field, file);
        }
      });
      let isBrandFormEmpty = true;

      for (let [key] of Array.from(brandFormData.entries())) {
        if (key !== 'partnerId' && key !== 'brandInfo') {
          isBrandFormEmpty = false;
          break;
        }
      }

      if (!isBrandFormEmpty) {
        try {
          const result: any = await dispatch(UpdateBrandInfo(brandFormData));
          if (result?.status === 200) {
            toast.success(
              'Thank you for submitting a branding change request. Your changes will be reviewed within 5 days.',
              {
                duration: 4000,
              }
            );
            setIsEditing(false);
          }
        } catch (error) {
          toast.error(
            error instanceof Error
              ? error.message
              : 'Failed to update brand information. Please try again.'
          );
        }
      } else {
        toast.error('Nothing changed to save');
      }
    }
  };
  useEffect(() => {
    if (!preview && !BrandInfoState?.logo) return;
    const img = new Image();
    img.onload = () => {
      if (img.width / img.height < 2 && img.width / img.height > 0.5) {
        setLogoX(-10);
      } else {
        setLogoX(10);
      }
    };
    img.src = preview || BrandInfoState?.logo;
  }, [preview, BrandInfoState?.logo]);

  const isApproved = BrandInfoState?.changeRequestDays;
  const isPending = BrandInfoState?.status === 'pending';

  const handleMouseEnter = (tooltipType: string) => {
    setActiveTooltip(tooltipType);
  };

  const handleMouseLeave = () => {
    setActiveTooltip(null);
  };
  const commonFileUploadProps = {
    isLoading: loadingBrandInfo,
    register,
    errors,
    watch,
    setError,
    clearErrors,
    setValue,
    accept: 'image/png, image/jpeg',
    maxFileSizeMB: 2,
  };

  return (
    <>
      <HistoryRouting history='Program Management/Branding' />
      <Toaster />
      {/* <PageShdowTitle title={'Branding'} /> */}
      <form onSubmit={handleSubmit(onsubmit)} className='bg-[#ffffff]'>
        {user?.admin && (
          <div
            className={`my-6 text-right flex ${isPending ? 'justify-between' : 'justify-end'} `}
          >
            {loadingStates['GetBrandInfoState'] ? (
              <Spinner />
            ) : (
              <>
                {isPending && (
                  <h4 className=' md:text-[0.75rem] xmd:text-[1.15rem]'>
                    Branding Status:{' '}
                    <span className='text-primary mt-[1px] font-semibold'>
                      {BrandInfoState?.status.charAt(0).toUpperCase() +
                        BrandInfoState?.status.slice(1)}
                    </span>
                  </h4>
                )}
                <div className='flex items-center gap-2'>
                  {BrandInfoState?.changeRequestDays !== undefined &&
                    BrandInfoState?.changeRequestDays !== null &&
                    BrandInfoState?.changeRequestDays > 0 && (
                      <div
                        className='relative group inline-block'
                        onMouseEnter={() =>
                          handleMouseEnter('changeRequestDays')
                        }
                        onMouseLeave={handleMouseLeave}
                      >
                        <BsInfoCircle
                          color='#1133BB'
                          size={22}
                          className='mt-1 cursor-pointer'
                        />
                        {activeTooltip === 'changeRequestDays' && (
                          <div className='absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                            <p className='text-sm text-center  whitespace-normal'>
                              Changes to branding will be permitted in{' '}
                              {BrandInfoState?.changeRequestDays} days . For
                              additional support please{' '}
                              <a
                                href='/support'
                                rel='noopener noreferrer'
                                className='text-primary font-medium underline'
                              >
                                contact us
                              </a>
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  <button
                    type='button'
                    onClick={handleEditToggle}
                    disabled={isApproved}
                    className='text-[#d50000] text-[1.25rem] pr-1 font-semibold underline disabled:opacity-50'
                  >
                    {isEditing ? 'Cancel' : 'Edit'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className='mb-1'>
          <div className=' justify-center max-w-[full] mx-auto primary-shadow rounded-lg'>
            <div className='bg-white primary-shadow rounded-lg w-full'>
              <div
                className={`mb-8 ${!isEditing ? 'pointer-events-none' : ''}`}
              >
                <div className='mb-6 pb-4'>
                  <h2 className='text-white bg-primary p-4 px-8 rounded-t-lg text-lg font-semibold'>
                    Branding
                  </h2>
                  <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                    <p className='text-base mb-8 text-[#343434] '>
                      Enter your organization&apos;s name to be displayed on the
                      app and website. Upload a high-resolution logo in either
                      JPEG or PNG format. Specify your brand&apos;s primary and
                      secondary colors using hex codes.
                    </p>
                    <div className='grid lg:grid-cols-2 gap-x-8 gap-y-4 pb-4 mb-6'>
                      {loadingBrandInfo ? (
                        <TextFieldLoader />
                      ) : (
                        <div>
                          <label className='block text-[#000000] text-sm font-[500] mb-2'>
                            Organization Display Name{' '}
                            <RequiredAsterisk isEditing={isEditing} />
                          </label>
                          <input
                            type='text'
                            id='orgDisplayName'
                            autoComplete='off'
                            readOnly={isApproved}
                            className={`register-input w-full  ${
                              !isEditing && 'read-only-input'
                            }`}
                            {...register('orgDisplayName', {
                              required: 'Organization display name is required',
                            })}
                          />

                          <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                            {errors.orgDisplayName?.message}
                          </p>
                        </div>
                      )}
                      {loadingBrandInfo ? (
                        <TextFieldLoader />
                      ) : (
                        <div>
                          <div className='flex justify-between items-center mb-2'>
                            <label className='block text-[#000000] text-sm font-[500]'>
                              Program URL{' '}
                              <RequiredAsterisk isEditing={isEditing} />
                            </label>
                            <div
                              className='relative group inline-block'
                              onMouseEnter={() =>
                                handleMouseEnter('programUrl')
                              }
                              onMouseLeave={handleMouseLeave}
                            >
                              <BsInfoCircle
                                color='#1133BB'
                                size={18}
                                className='pointer-events-auto cursor-pointer'
                              />
                              {activeTooltip === 'programUrl' && (
                                <div className='hidden group-hover:block absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                                  <p className='text-sm text-center whitespace-normal'>
                                    This is the website of your Savers App
                                    program. Once the program is live, this can
                                    no longer be changed.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className='flex my-3'>
                            <Controller
                              control={control}
                              name='b2cLink'
                              defaultValue={extractB2CLinkPrefix(
                                BrandInfoState?.b2cLink
                              )}
                              rules={{
                                required: 'Program URL is required',
                              }}
                              render={({ field }) => (
                                <input
                                  type='text'
                                  id='b2cLink'
                                  autoComplete='off'
                                  placeholder='Your Program URL'
                                  className='register-input w-full rounded-r-none my-0'
                                  readOnly
                                  {...field}
                                />
                              )}
                            />
                            <div className='flex items-center px-3 bg-gray-100 border border-l-0 border-gray-300 rounded-r-md text-gray-700 text-sm'>
                              .saversapp.com
                            </div>
                          </div>
                          {storedB2cDomain && (
                            <a
                              href={storedB2cHref}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-[0.75rem] font-semibold text-primary mt-[5px] pointer-events-auto'
                            >
                              {storedB2cDomain}
                            </a>
                          )}

                          {errors.b2cLink && (
                            <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                              {errors.b2cLink.message}
                            </p>
                          )}
                        </div>
                      )}
                      {loadingBrandInfo ? (
                        <TextFieldLoader />
                      ) : (
                        <div>
                          <div className='flex justify-between items-center mb-2'>
                            <label className='block text-[#000000] text-sm font-[500]'>
                              Referral Link{' '}
                              <RequiredAsterisk isEditing={isEditing} />
                            </label>
                            <div
                              className='relative group inline-block'
                              onMouseEnter={() =>
                                handleMouseEnter('referralLink')
                              }
                              onMouseLeave={handleMouseLeave}
                            >
                              <BsInfoCircle
                                color='#1133BB'
                                size={18}
                                className='pointer-events-auto cursor-pointer'
                              />
                              {activeTooltip === 'referralLink' && (
                                <div className='hidden group-hover:block absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                                  <p className='text-sm  whitespace-normal'>
                                    This alias will be appended to the link that
                                    will be shared for referrals. Once the
                                    program is live, this can no longer be
                                    changed.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>

                          <div className='flex my-3'>
                            <div className='flex items-center px-3 bg-gray-100 border border-r-0 border-gray-300 rounded-l-md text-gray-700 text-sm'>
                              https://earncashback.app.link/
                            </div>

                            <Controller
                              control={control}
                              name='appDeepLink'
                              defaultValue={BrandInfoState?.appDeepLink || ''}
                              rules={{
                                required: 'Referral Link is required',
                              }}
                              render={({ field }) => (
                                <input
                                  type='text'
                                  id='appDeepLink'
                                  autoComplete='off'
                                  placeholder='Your Referral Link'
                                  className='register-input w-full rounded-l-none my-0'
                                  readOnly
                                  {...field}
                                />
                              )}
                            />
                          </div>
                          {BrandInfoState?.appDeepLink && (
                            <a
                              href={BrandInfoState?.appDeepLink}
                              target='_blank'
                              rel='noopener noreferrer'
                              className='text-[0.75rem] font-semibold text-primary mt-[5px] pointer-events-auto'
                            >
                              {BrandInfoState?.appDeepLink}
                            </a>
                          )}

                          {errors.appDeepLink && (
                            <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                              {errors.appDeepLink.message}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    <div className='grid lg:grid-cols-2 gap-x-8 gap-y-4 pb-4 mb-6'>
                      <FileUpload
                        {...commonFileUploadProps}
                        name='logo'
                        label='Upload Logo (optimal dimension is 800 * 400)'
                        description="Upload your organization's logo to be featured in the app. For the best results, use a transparent background and remove/crop any blank border around the logo"
                        backendFileName={
                          BrandInfoState?.logo
                            ? extractFileName(BrandInfoState?.logo)
                            : ''
                        }
                        isRequired={isEditing}
                        readOnly={isApproved}
                        isEditing={isEditing}
                        requiredMessage='Logo is required'
                        backendFile={BrandInfoState?.logo}
                        setPreview={setPreview}
                        onFileReady={file => {
                          const previewURL = URL.createObjectURL(file);
                          setPreview(previewURL);
                        }}
                      />
                      {/* <FileUpload
                        {...commonFileUploadProps}
                        name='logoMobile'
                        label='Upload Mobile Logo (optimal dimension is 800 * 400)'
                        isEditing={isEditing}
                        readOnly={isApproved}
                        backendFileName={
                          BrandInfoState?.logoMobile
                            ? extractFileName(BrandInfoState?.logoMobile)
                            : ''
                        }
                        description='Optionally, upload a smaller logo or icon for optimal display on mobile screens.'
                      /> */}
                      <FileUpload
                        {...commonFileUploadProps}
                        name='logoMonochrome'
                        label='Upload Monochrome Logo (optimal dimension is 800 * 400)'
                        backendFileName={
                          BrandInfoState?.logoMonochrome
                            ? extractFileName(BrandInfoState?.logoMonochrome)
                            : ''
                        }
                        isEditing={isEditing}
                        readOnly={isApproved}
                      />
                      <FileUpload
                        {...commonFileUploadProps}
                        name='icon'
                        label='Upload Favicon (optimal dimension is 200 * 200)'
                        isEditing={isEditing}
                        readOnly={isApproved}
                        dimentions={{ width: 200, height: 200 }}
                        backendFileName={
                          BrandInfoState?.icon
                            ? extractFileName(BrandInfoState?.icon)
                            : ''
                        }
                      />
                      {/* <FileUpload
                        {...commonFileUploadProps}
                        name='logoNewsletter'
                        label='upload Email/Newsletter logo (optimal dimension is 160 * 80)'
                        dimentions={{ width: 160, height: 80 }}
                        backendFileName={
                          BrandInfoState?.logoNewsletter
                            ? extractFileName(BrandInfoState?.logoNewsletter)
                            : ''
                        }
                        isEditing={isEditing}
                        readOnly={isApproved}
                      /> */}
                      <FileUpload
                        {...commonFileUploadProps}
                        name='logoBanner'
                        label='Upload Banner Image (optimal dimension is 400 * 190)'
                        dimentions={{ width: 400, height: 190 }}
                        backendFileName={
                          BrandInfoState?.logoBanner
                            ? extractFileName(BrandInfoState?.logoBanner)
                            : ''
                        }
                        backendFile={BrandInfoState?.logoBanner}
                        moreDetails='This is OPTIONAL. If uploaded, this banner will be displayed with other banners in the app’s home page carousel. This can be used by the program to promote your brand or products & services. If clicked, the banner will lead to your brand description page in the app.'
                        setPreview={setPreviewBanner}
                        onFileReady={file => {
                          const previewURL = URL.createObjectURL(file);
                          setPreviewBanner(previewURL);
                        }}
                        isEditing={isEditing}
                        readOnly={isApproved}
                      />
                      {loadingBrandInfo ? (
                        <TextFieldLoader />
                      ) : (
                        <div>
                          <div className='flex justify-between items-center mb-2'>
                            <label className='block text-[#000000] text-sm font-[500]'>
                              Visit Website URL
                              <RequiredAsterisk isEditing={isEditing} />
                            </label>
                            <div
                              className='relative group inline-block'
                              onMouseEnter={() =>
                                handleMouseEnter('visitWebsite')
                              }
                              onMouseLeave={handleMouseLeave}
                            >
                              <BsInfoCircle
                                color='#1133BB'
                                size={18}
                                className='pointer-events-auto cursor-pointer'
                              />
                              {activeTooltip === 'visitWebsite' && (
                                <div className='hidden group-hover:block absolute top-full right-0 mt-2 w-60 bg-white border border-gray-300 rounded-md shadow-md z-10 p-2'>
                                  <p className='text-sm whitespace-normal'>
                                    This is an optional URL that can be embedded
                                    in the “Visit Website” button at the bottom
                                    of your brand description page (shown in the
                                    preview below). If not provided, the Program
                                    URL will be embedded instead.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                          <input
                            type='text'
                            id='visitWebsiteLink'
                            readOnly={isApproved}
                            placeholder='Your Visit Website URL'
                            className={`register-input w-full ${
                              !isEditing && 'read-only-input'
                            }`}
                            {...register('visitWebsiteLink', {
                              required: 'Visit Website URL is required',
                            })}
                          />
                          <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                            {errors.visitWebsiteLink?.message}
                          </p>
                        </div>
                      )}

                      {/* {logoBanner &&
                        (logoBanner.length > 0 ||
                          BrandInfoState?.logoBanner) && (
                          <div>
                            {loadingBrandInfo ? (
                              <TextFieldLoader />
                            ) : (
                              <div>
                                <label className='block text-[#000000] text-sm font-[500] mb-2'>
                                  Banner URL
                                </label>
                                <input
                                  type='text'
                                  id='logoBannerUrl'
                                  readOnly={isApproved}
                                  placeholder='Your Banner URL'
                                  className={`register-input w-full ${
                                    !isEditing && 'read-only-input'
                                  }`}
                                  {...register('logoBannerUrl')}
                                />
                                <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                                  {errors.logoBannerUrl?.message}
                                </p>
                              </div>
                            )}
                          </div>
                        )} */}
                    </div>
                  </div>
                  {/* Row 2 */}
                  <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 '>
                    <div>
                      <div className='mb-0 mt-4 px-4 md:px-8 md:py-4'>
                        <div className='grid grid-cols-1 gap-x-8 gap-y-4'>
                          <ColorInput<BrandingFormData>
                            name={'primaryColor' as keyof BrandingFormData}
                            label='Primary Color (Hex)'
                            defaultColor='#1133BB'
                            register={register}
                            isLoading={loadingBrandInfo}
                            isEditing={isEditing}
                            readOnly={isApproved}
                            watch={watch}
                            setValue={setValue}
                            error={errors.primaryColor?.message}
                            description='Used for landing page background and selected
                              icons and headers'
                          />

                          <ColorInput<BrandingFormData>
                            name={'secondaryColor' as keyof BrandingFormData}
                            label='Secondary Color (Hex) '
                            defaultColor='#00C389'
                            register={register}
                            isLoading={loadingBrandInfo}
                            isEditing={isEditing}
                            readOnly={isApproved}
                            watch={watch}
                            setValue={setValue}
                            error={errors.secondaryColor?.message}
                            description='Used for secondary icons and titles'
                          />

                          <ColorInput<BrandingFormData>
                            name={'tertiaryColor' as keyof BrandingFormData}
                            label='Third Color (Hex) '
                            defaultColor='#1133BB'
                            register={register}
                            isLoading={loadingBrandInfo}
                            isEditing={isEditing}
                            readOnly={isApproved}
                            watch={watch}
                            setValue={setValue}
                            error={errors.tertiaryColor?.message}
                            description='Used for category icons and text'
                          />
                          <ColorInput<BrandingFormData>
                            name={'quaternaryColor' as keyof BrandingFormData}
                            label='Fourth Color (Hex) '
                            defaultColor='#1133BB'
                            register={register}
                            isLoading={loadingBrandInfo}
                            isEditing={isEditing}
                            readOnly={isApproved}
                            watch={watch}
                            setValue={setValue}
                            error={errors.quaternaryColor?.message}
                            description='Used for action buttons'
                          />
                        </div>
                      </div>
                      <div className='mb-0 mt-4 px-4 md:px-8'>
                        <div className='grid grid-cols-1 gap-x-8 gap-y-4 pb-4 mb-6'>
                          {loadingBrandInfo ? (
                            <TextFieldLoader />
                          ) : (
                            <div>
                              <label className='block text-[#000000] text-sm font-[500] mb-2'>
                                Tag Line / Slogan (30 character max){' '}
                                <RequiredAsterisk isEditing={isEditing} />
                              </label>
                              <input
                                type='text'
                                id='tag'
                                autoComplete='off'
                                maxLength={30}
                                readOnly={isApproved}
                                className={`register-input w-full ${
                                  !isEditing && 'read-only-input'
                                }`}
                                {...register('tag', {
                                  required: 'Tag is required',
                                })}
                              />
                              {tagValue.length >= 30 && isEditing ? (
                                <p className='text-[0.75rem] text-red-600 font-medium mt-1'>
                                  You’ve reached the maximum allowed characters.
                                </p>
                              ) : (
                                <p className='text-[0.75rem] text-[#878787] '>
                                  Add your program’s tag line or slogan, or
                                  select one of the provided options.
                                </p>
                              )}
                              <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                                {errors.tag?.message}
                              </p>
                              <div className='flex flex-wrap gap-2 mt-2'>
                                {tagSuggestions.map(suggestion => (
                                  <button
                                    key={suggestion}
                                    type='button'
                                    className='px-3 py-1 bg-gray-200 hover:bg-gray-300 text-sm rounded-lg text-gray-700 border border-gray-300 transition'
                                    onClick={() =>
                                      !isApproved && setValue('tag', suggestion)
                                    }
                                  >
                                    {suggestion}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {loadingBrandInfo ? (
                            <TextFieldLoader />
                          ) : (
                            <div>
                              <label className='block text-[#000000] text-sm font-[500] mb-2'>
                                description (200 characters max){' '}
                                <RequiredAsterisk isEditing={isEditing} />
                              </label>
                              <textarea
                                id='description'
                                className={`register-input w-full ${
                                  !isEditing && 'read-only-input'
                                }`}
                                maxLength={200}
                                rows={4.5}
                                readOnly={isApproved}
                                autoComplete='off'
                                {...register('description', {
                                  required: 'description is required',
                                })}
                              />
                              {descValue.length >= 200 && isEditing ? (
                                <p className='text-[0.75rem] text-red-600 font-medium mt-1'>
                                  You’ve reached the maximum allowed characters.
                                </p>
                              ) : (
                                <p className='text-[0.75rem] text-[#878787] '>
                                  Add a description of your program and/or brand
                                  for your members to learn more about you , or
                                  select one of the provided options.
                                </p>
                              )}

                              <p className='text-[0.75rem] font-semibold text-[#d90000] mt-[5px]'>
                                {errors.description?.message}
                              </p>
                              <div className='flex flex-wrap gap-2 mt-2'>
                                {descriptionSuggestions.map(suggestion => (
                                  <button
                                    key={suggestion}
                                    type='button'
                                    className='px-3 py-1 bg-gray-200 hover:bg-gray-300 text-sm rounded-lg text-gray-700 border border-gray-300 text-left'
                                    onClick={() =>
                                      !isApproved &&
                                      setValue('description', suggestion)
                                    }
                                  >
                                    {suggestion}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className='place-items-center'>
                      <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-1 xmd:grid-cols-2 gap-10 py-10 pl-5 pr-4 max-w-xl rounded-lg lg:grid-cols-1 xsl:grid-cols-2'>
                        {loadingBrandInfo ? (
                          <AppPreviewPair brand={{}} loading />
                        ) : (
                          <AppPreviewPair
                            brand={{
                              primary:
                                watch('primaryColor') ||
                                BrandInfoState?.primaryColor ||
                                '#1133BB',
                              secondary:
                                watch('secondaryColor') ||
                                BrandInfoState?.secondaryColor ||
                                '#00C389',
                              tertiary:
                                watch('tertiaryColor') ||
                                BrandInfoState?.tertiaryColor ||
                                '#1133BB',
                              quaternary:
                                watch('quaternaryColor') ||
                                BrandInfoState?.quaternaryColor ||
                                '#00C389',
                              logo: preview || BrandInfoState?.logo || '',
                              orgName: watch('orgDisplayName') || '',
                              tagLine: watch('tag') || '',
                              description: watch('description') || '',
                              banner:
                                previewBanner ||
                                BrandInfoState?.logoBanner ||
                                'https://s3.us-east-1.amazonaws.com/assets.savers.club/partner-assets/image.png',
                              partnerId,
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* <BrandingInformation brandInfoPage={true} isEditing={isEditing} /> */}

          <div className='w-full flex justify-end gap-4'>
            {isEditing && (
              <>
                <Button
                  type='button'
                  onClick={() => {
                    setIsEditing(false);
                  }}
                  className={`w-full  text-sm px-5 text-primary bg-gray-200 border-primaryBg-100 min-w-[7rem] py-2 md:w-[7rem] rounded-[10px] font-semibold`}
                >
                  Cancel
                </Button>

                <Button
                  type='submit'
                  disabled={loadingStates['UpdateBrandInfoState']}
                  className={`w-full text-sm px-5 text-white bg-primary border-primary min-w-[7rem] py-2 md:w-[7.5rem] rounded-[10px] font-semibold`}
                >
                  {loadingStates['UpdateBrandInfoState']
                    ? 'Saving...'
                    : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default OrgData;
